import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import type { LucideIcon } from 'lucide-react'
import {
  Archive,
  BadgeCheck,
  Check,
  CheckCircle,
  CheckCircle2,
  Copy,
  Download,
  Edit,
  Eye,
  ExternalLink,
  FileEdit,
  History,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { AppIcon, type AppIconVariant } from '@/modules/shared/components/ui/AppIcon'
import { AppButtonLoader } from '@/modules/shared/components/ui/AppLoading'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/shared/components/ui/tooltip'

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent text-sm font-medium leading-none ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-[busy=true]:pointer-events-none aria-[busy=true]:opacity-90 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover',
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-primary-soft hover:text-primary',
        success: 'bg-success text-success-foreground shadow-sm hover:bg-success/90',
        warning: 'bg-warning text-warning-foreground shadow-sm hover:bg-warning/90',
        danger: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border-border bg-background text-foreground shadow-sm hover:border-primary/30 hover:bg-primary-soft hover:text-primary',
        ghost: 'bg-transparent text-foreground shadow-none hover:bg-muted hover:text-foreground',
        'danger-ghost': 'bg-transparent text-destructive shadow-none hover:bg-destructive-soft hover:text-destructive',
        link: 'h-auto min-h-0 border-transparent bg-transparent px-0 py-0 text-primary underline-offset-4 shadow-none hover:underline',
      },
      size: {
        default: 'min-h-[48px] px-4 py-2 sm:h-9 sm:min-h-0 sm:px-4',
        sm: 'min-h-[48px] px-3 py-2 text-xs sm:h-8 sm:min-h-0 sm:px-3',
        lg: 'min-h-[48px] px-6 py-2 sm:h-10 sm:min-h-0 sm:px-6',
        icon: 'min-h-[48px] min-w-[48px] px-0 py-0 sm:h-8 sm:min-h-0 sm:w-8',
        xs: 'min-h-[48px] px-2.5 py-2 text-[11px] sm:h-7 sm:min-h-0 sm:px-2.5',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
    },
  },
)

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>

const BUTTON_CONTENT_CLASS = 'ui-button-content'

function getLoadingIconVariant(_variant: ButtonVariant | null | undefined): AppIconVariant {
  return 'inherit'
}

function getIconGhostVariant(): AppIconVariant {
  return 'inherit'
}

function wrapButtonContent(children: React.ReactNode) {
  return <span className={`${BUTTON_CONTENT_CLASS} inline-flex items-center gap-1.5`}>{children}</span>
}

function renderButtonFallback(
  className: string,
  ref: React.ForwardedRef<HTMLButtonElement>,
  isDisabled: boolean,
  loading: boolean,
  type: React.ButtonHTMLAttributes<HTMLButtonElement>['type'],
  children: React.ReactNode,
  variant: ButtonVariant | null | undefined,
  props: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'disabled' | 'type'>,
) {
  return (
    <button
      className={className}
      ref={ref}
      disabled={isDisabled}
      aria-busy={loading}
      data-loading={loading ? 'true' : undefined}
      type={type ?? 'button'}
      {...props}
    >
      {loading ? (
        <AppButtonLoader className={cn(getLoadingIconVariant(variant) === 'inherit' ? 'bg-current/35' : '')} />
      ) : null}
      {wrapButtonContent(children)}
    </button>
  )
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      className,
      children,
      disabled,
      fullWidth,
      loading = false,
      size,
      type,
      variant,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = disabled || loading
    const resolvedClassName = cn(buttonVariants({ variant, size, fullWidth, className }))

    if (asChild) {
      if (React.Children.count(children) !== 1) {
        return renderButtonFallback(resolvedClassName, ref, isDisabled, loading, type, children, variant, props)
      }

      const onlyChild = React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>

      if (!React.isValidElement(onlyChild)) {
        return renderButtonFallback(resolvedClassName, ref, isDisabled, loading, type, children, variant, props)
      }

      return (
        <Comp
          className={resolvedClassName}
          ref={ref}
          aria-busy={loading}
          data-loading={loading ? 'true' : undefined}
          {...props}
        >
          {React.cloneElement(onlyChild, {
            children: wrapButtonContent(onlyChild.props.children),
          })}
        </Comp>
      )
    }

    return (
      <Comp
        className={resolvedClassName}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        data-loading={loading ? 'true' : undefined}
        type={type ?? 'button'}
        {...props}
      >
        {loading ? (
          <AppButtonLoader className={cn(getLoadingIconVariant(variant) === 'inherit' ? 'bg-current/35' : '')} />
        ) : null}
        {wrapButtonContent(children)}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export interface ButtonWithIconProps extends Omit<ButtonProps, 'children'> {
  icon: LucideIcon
  children: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const ButtonWithIcon = React.forwardRef<HTMLButtonElement, ButtonWithIconProps>(
  (
    {
      asChild = false,
      children,
      icon,
      iconPosition = 'left',
      loading = false,
      ...props
    },
    ref,
  ) => {
    const iconNode = !loading ? (
      <AppIcon
        icon={icon}
        size="button"
        variant="inherit"
        className="shrink-0"
      />
    ) : (
      <AppButtonLoader className="shrink-0 bg-current/35" />
    )

    if (asChild) {
      const onlyChild = React.Children.count(children) === 1
        ? (React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>)
        : null

      if (React.isValidElement(onlyChild)) {
        const content = onlyChild.props.children

        return (
          <Button ref={ref} asChild {...props} aria-busy={loading}>
            {React.cloneElement(onlyChild, {
              children: wrapButtonContent(
                <>
                  {iconPosition === 'left' ? iconNode : null}
                  <span className="inline-flex items-center justify-center align-middle">{content}</span>
                  {iconPosition === 'right' ? iconNode : null}
                </>,
              ),
            })}
          </Button>
        )
      }
    }

    return (
      <Button ref={ref} loading={loading} asChild={false} {...props}>
        {iconPosition === 'left' ? iconNode : null}
        <span className="inline-flex items-center justify-center align-middle">{children}</span>
        {iconPosition === 'right' ? iconNode : null}
      </Button>
    )
  },
)
ButtonWithIcon.displayName = 'ButtonWithIcon'

/**
 * Icon intent for semantic color mapping
 * - view: gray (text-gray-600 → hover:text-gray-700)
 * - edit: blue (text-blue-600 → hover:text-blue-700)
 * - delete: red (text-red-600 → hover:text-red-700)
 * - activate: green (text-green-600 → hover:text-green-700)
 * - warning: yellow (text-yellow-600 → hover:text-yellow-700)
 * - default: inherits from parent
 */
export type IconIntent = 'view' | 'edit' | 'delete' | 'activate' | 'warning' | 'default'

const ICON_INTENT_CLASSES: Record<IconIntent, string> = {
  view: 'text-gray-600 hover:text-gray-700',
  edit: 'text-blue-600 hover:text-blue-700',
  delete: 'text-red-600 hover:text-red-700',
  activate: 'text-green-600 hover:text-green-700',
  warning: 'text-yellow-600 hover:text-yellow-700',
  default: 'text-foreground hover:text-foreground',
}

type IconGhostButtonDensity = 'default' | 'dense'

const VIEW_KEYWORDS = ['view', 'preview', 'open', 'details', 'history', 'copy']
const EDIT_KEYWORDS = ['edit', 'create', 'new', 'clone', 'download', 'export', 'retry']
const DELETE_KEYWORDS = ['delete', 'remove']
const ACTIVATE_KEYWORDS = ['activate', 'publish', 'approve', 'complete', 'enable']
const WARNING_KEYWORDS = ['archive', 'warning', 'suspend', 'reject']

const VIEW_ICONS = new Set([Eye, ExternalLink, History, MoreHorizontal, MoreVertical, Copy])
const EDIT_ICONS = new Set([Pencil, Edit, Plus, FileEdit, Download, RefreshCw])
const DELETE_ICONS = new Set([Trash2])
const ACTIVATE_ICONS = new Set([Check, CheckCircle, CheckCircle2, BadgeCheck, Play])
const WARNING_ICONS = new Set([Archive])

function resolveIntentFromText(text?: string): IconIntent {
  if (!text) return 'default'
  const normalized = text.trim().toLowerCase()

  if (DELETE_KEYWORDS.some((keyword) => normalized.includes(keyword))) return 'delete'
  if (ACTIVATE_KEYWORDS.some((keyword) => normalized.includes(keyword))) return 'activate'
  if (WARNING_KEYWORDS.some((keyword) => normalized.includes(keyword))) return 'warning'
  if (EDIT_KEYWORDS.some((keyword) => normalized.includes(keyword))) return 'edit'
  if (VIEW_KEYWORDS.some((keyword) => normalized.includes(keyword))) return 'view'

  return 'default'
}

function resolveIntentFromIcon(icon: LucideIcon): IconIntent {
  if (DELETE_ICONS.has(icon)) return 'delete'
  if (ACTIVATE_ICONS.has(icon)) return 'activate'
  if (WARNING_ICONS.has(icon)) return 'warning'
  if (EDIT_ICONS.has(icon)) return 'edit'
  if (VIEW_ICONS.has(icon)) return 'view'
  return 'default'
}

function resolveIconIntent(intent: IconIntent, icon: LucideIcon, tooltipText?: string): IconIntent {
  if (intent !== 'default') return intent

  const fromText = resolveIntentFromText(tooltipText)
  if (fromText !== 'default') return fromText

  return resolveIntentFromIcon(icon)
}

export interface IconGhostButtonProps
  extends Omit<ButtonProps, 'children' | 'size' | 'variant' | 'asChild'> {
  icon: LucideIcon
  tooltip?: string
  label?: string
  to?: string
  variant?: 'ghost' | 'danger-ghost'
  tooltipSide?: React.ComponentProps<typeof TooltipContent>['side']
  density?: IconGhostButtonDensity
  /**
   * Semantic intent for icon color.
   * @default 'default'
   */
  intent?: IconIntent
}

const IconGhostButton = React.forwardRef<HTMLButtonElement, IconGhostButtonProps>(
  (
    {
      className,
      icon,
      tooltip,
      label,
      loading = false,
      to,
      tooltipSide = 'top',
      variant = 'ghost',
      density = 'default',
      intent = 'default',
      ...props
    },
    ref,
  ) => {
    const tooltipText = tooltip ?? label
    const ariaLabel = label ?? tooltipText

    if (!tooltipText) {
      return null
    }

    const resolvedIntent = resolveIconIntent(intent, icon, tooltipText)
    const intentClass = ICON_INTENT_CLASSES[resolvedIntent]
    const iconVariant = resolvedIntent === 'default' ? getIconGhostVariant() : 'inherit'
    const iconContent = <AppIcon icon={icon} size="button" variant={iconVariant} className={resolvedIntent !== 'default' ? intentClass : undefined} />
    const densityClass = density === 'dense'
      ? 'h-7 w-7 sm:h-7 sm:w-7'
      : 'h-8 w-8 sm:h-8 sm:w-8'

    const buttonClassName = cn(
      'inline-flex items-center justify-center rounded-md p-1 transition-colors hover:bg-surface-hover',
      '!min-h-0 !min-w-0 px-0 py-0',
      densityClass,
      intentClass,
      className
    )

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      if (!to) {
        props.onClick?.(event)
        return
      }

      props.onClick?.(event)
      if (!event.defaultPrevented) {
        window.location.assign(to)
      }
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            variant={variant}
            size="icon"
            loading={loading}
            aria-label={ariaLabel}
            className={buttonClassName}
            {...props}
            onClick={handleClick}
          >
            {iconContent}
          </Button>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{tooltipText}</TooltipContent>
      </Tooltip>
    )
  },
)
IconGhostButton.displayName = 'IconGhostButton'

export { Button, ButtonWithIcon, IconGhostButton, buttonVariants }
export type { ButtonSize, ButtonVariant }
